<template>
  <RfDialog
    v-if="props.show"
    ref="dialog"
    title="Bulk Edit"
    width="600"
    confirmText="Apply"
    keepOpenOnConfirm
    :loading="isProcessing"
    :disabled="!hasChanges"
    @confirm="handleApply"
    @close="emit('close')"
  >
    <div
      class="relative"
      :class="tagsEnabled && engagementCategoriesEnabled ? 'min-h-100' : 'min-h-62.5'"
    >
      <!-- Loading overlay -->
      <div
        v-if="isProcessing"
        class="absolute inset-0 z-50 flex flex-col items-center justify-center rounded bg-white/90 backdrop-blur-sm"
      >
        <RfProgressActivityIcon class="h-16! w-16! animate-spin" />
        <div class="text-body mt-4 font-semibold">
          Editing {{ selectedCount }} {{ itemType }}{{ selectedCount > 1 ? "s" : "" }}...
        </div>
        <div class="text-caption text-grey-2 mt-2">Please wait, this may take a moment</div>
      </div>

      <div class="text-body mb-4">
        Edit attributes for
        <b>{{ selectedCount }} selected {{ itemType }}{{ selectedCount > 1 ? "s" : "" }}</b>
      </div>

      <!-- Tags Section -->
      <div v-if="tagsEnabled" class="mb-6 flex flex-col gap-2">
        <div class="text-body flex items-center gap-2 font-semibold">
          <RfTagIcon class="h-5! w-5!" />
          Tags
        </div>

        <!-- Add Tags -->
        <RfSelect
          v-model="tagsToAdd"
          title="Add Tags"
          class="w-full"
          variant="outlined"
          :items="filteredAvailableTagsForAdd"
          itemKey="id"
          itemValue="title"
          placeholder="Select tags to add"
          multiple
          clearable
          autoWidth
          sameWidth
          :disabled="isProcessing"
        >
          <template #item="{ item }">
            <RfColorTag :backgroundColor="item.color" :title="item.title" pill disabled />
          </template>
          <template #selected="{ props: ids }">
            <span class="inline-flex flex-wrap items-center gap-1">
              <RfColorTag
                v-for="tag in ids
                  .map(id => props.availableTags.find(t => t.id === id))
                  .filter(Boolean)"
                :key="tag.id"
                :backgroundColor="tag.color"
                :title="tag.title"
                pill
              />
            </span>
          </template>
        </RfSelect>

        <!-- Remove Tags -->
        <RfSelect
          v-model="tagsToRemove"
          title="Remove Tags"
          size="small"
          variant="outlined"
          :items="filteredTagsOnSelectedItemsForRemove"
          itemKey="id"
          itemValue="title"
          placeholder="Select tags to remove"
          multiple
          clearable
          autoWidth
          sameWidth
          class="w-full"
          :disabled="
            (filteredTagsOnSelectedItemsForRemove.length === 0 &&
              tagsToAdd.length === 0 &&
              `No tags available to remove from selected ${itemType}s`) ||
            isProcessing
          "
        >
          <template #item="{ item }">
            <RfColorTag :backgroundColor="item.color" :title="item.title" pill disabled />
          </template>
          <template #selected="{ props: ids }">
            <span class="inline-flex flex-wrap items-center gap-1">
              <RfColorTag
                v-for="tag in ids
                  .map(id => props.availableTags.find(t => t.id === id))
                  .filter(Boolean)"
                :key="tag.id"
                :backgroundColor="tag.color"
                :title="tag.title"
                pill
              />
            </span>
          </template>
        </RfSelect>
      </div>

      <!-- Engagement Category Section -->
      <div v-if="engagementCategoriesEnabled">
        <div class="text-body mb-3 flex items-center gap-2 font-semibold">
          <RfFolderIcon class="h-5! w-5!" />
          Engagement Category
        </div>

        <RfSelect
          v-model="categoryModel"
          variant="outlined"
          :items="engagementCategories"
          itemKey="value"
          class="w-full"
          itemValue="label"
          returnObject
          placeholder="Select engagement category (optional)"
          hintMessage="Leave empty to keep existing categories"
          clearable
          autoWidth
          sameWidth
          :disabled="isProcessing"
          :itemDisabled="item => item.disabled"
          :itemDisabledMessage="item => item.disabledMessage"
        >
          <template #selected="{ props: category }">
            <RfColorTag
              v-if="category.value"
              :backgroundColor="getCategoryColor(category.value)"
              :title="getCategoryLabel(category.value)"
              pill
              disabled
            />
            <span v-else>{{ category.label }}</span>
          </template>
          <template #item="{ item }">
            <RfColorTag
              v-if="item.value"
              :backgroundColor="getCategoryColor(item.value)"
              :title="getCategoryLabel(item.value)"
              pill
              disabled
            />
            <span v-else class="text-body">{{ item.label }}</span>
          </template>
        </RfSelect>
        <RfRadioGroup
          v-if="hasSubcategories(selectedCategory)"
          v-model="subcategoryRadio"
          legend="Retention Type"
          vertical
          :options="subcategoryRadioOptions"
          :disabled="isProcessing"
          class="mt-4"
        />
      </div>
    </div>
  </RfDialog>
</template>

<script setup>
import { ref, computed, watch, nextTick } from "vue";
import RfDialog from "@/components/modals/RfDialog.vue";
import RfSelect from "@/components/inputs/RfSelect.vue";
import RfColorTag from "@/components/tags/RfColorTag.vue";
import RfTagIcon from "@/components/icons/RfTagIcon.vue";
import RfFolderIcon from "@/components/icons/RfFolderIcon.vue";
import RfProgressActivityIcon from "@/components/icons/RfProgressActivityIcon.vue";
import RfRadioGroup from "@/components/inputs/RfRadioGroup.vue";
import { getCategoryColor, getCategoryLabel } from "@/utils/constants/EngagementCategoryConstants";
import { useEngagementSubcategories } from "@/utils/composables/useEngagementSubcategories";

const props = defineProps({
  show: { type: Boolean, default: false },
  selectedCount: { type: Number, required: true },
  selectedItems: { type: Array, default: () => [] },
  availableTags: { type: Array, default: () => [] },
  engagementCategories: { type: Array, default: () => [] },
  engagementCategoriesEnabled: { type: Boolean, default: false },
  tagsEnabled: { type: Boolean, default: true },
  itemType: { type: String, default: "item" }, // 'prompt' or 'guide'
});

const emit = defineEmits(["close", "apply"]);

const dialog = ref(null);
const tagsToAdd = ref([]);
const tagsToRemove = ref([]);
const selectedCategory = ref(undefined);
const selectedSubcategory = ref(undefined);
const isProcessing = ref(false);

const { radioOptionsForCategory, hasSubcategories, isSubcategoryAllowedFor } =
  useEngagementSubcategories();

const subcategoryRadio = computed({
  get: () =>
    selectedSubcategory.value === undefined ? "keep" : (selectedSubcategory.value ?? "none"),
  set: v => {
    selectedSubcategory.value = v === "keep" ? undefined : v === "none" ? null : v;
  },
});

const subcategoryRadioOptions = computed(() => [
  {
    value: "keep",
    label: "Keep existing",
    description: "Leave subcategories unchanged",
  },
  ...radioOptionsForCategory(selectedCategory.value),
]);

watch(selectedCategory, category => {
  if (!isSubcategoryAllowedFor(category, selectedSubcategory.value))
    selectedSubcategory.value = undefined;
});

// The select holds whole category items (returnObject) so a picked "None" (value: null)
// renders as a real selection instead of falling back to the placeholder; "" from the
// clear icon maps back to "untouched" (undefined), which the Apply gating relies on.
const categoryModel = computed({
  get: () =>
    selectedCategory.value === undefined
      ? ""
      : (props.engagementCategories.find(c => c.value === selectedCategory.value) ?? ""),
  set: v => {
    if (v === "" || v == null) selectedCategory.value = undefined;
    else selectedCategory.value = v.value;
  },
});

// Filter "Remove" dropdown to show only tags that exist on selected items
const tagsOnSelectedItems = computed(() => {
  if (!props.selectedItems.length) return [];

  const tagIdSet = new Set();
  props.selectedItems.forEach(item => {
    if (item.tags && Array.isArray(item.tags)) {
      item.tags.forEach(tag => tagIdSet.add(tag.id));
    }
  });

  return props.availableTags.filter(tag => tagIdSet.has(tag.id));
});

// Filter "Add" dropdown to exclude tags selected for removal
const filteredAvailableTagsForAdd = computed(() => {
  return props.availableTags.filter(tag => !tagsToRemove.value.includes(tag.id));
});

// Filter "Remove" dropdown to exclude tags selected for addition
const filteredTagsOnSelectedItemsForRemove = computed(() => {
  return tagsOnSelectedItems.value.filter(tag => !tagsToAdd.value.includes(tag.id));
});

const hasChanges = computed(() => {
  const tagChanges =
    props.tagsEnabled && (tagsToAdd.value.length > 0 || tagsToRemove.value.length > 0);
  const categoryChanges = props.engagementCategoriesEnabled && selectedCategory.value !== undefined;
  return tagChanges || categoryChanges;
});

const handleApply = () => {
  if (isProcessing.value || !hasChanges.value) return;

  isProcessing.value = true;

  const result = {};

  // Only include tags if enabled
  if (props.tagsEnabled) {
    result.tagsToAdd = tagsToAdd.value;
    result.tagsToRemove = tagsToRemove.value;
  }

  // Only include category if enabled
  if (props.engagementCategoriesEnabled) {
    result.engagement_category = selectedCategory.value;
    if (hasSubcategories(selectedCategory.value)) {
      if (selectedSubcategory.value !== undefined) {
        result.engagement_subcategory = selectedSubcategory.value;
      }
    } else if (selectedCategory.value !== undefined) {
      result.engagement_subcategory = null;
    }
  }

  emit("apply", result);
};

// Watch for show prop changes and automatically show/hide the modal
watch(
  () => props.show,
  newVal => {
    if (newVal) {
      nextTick(() => {
        dialog.value?.open();
      });
    }
  },
);

// Expose methods and state for parent component
defineExpose({
  show: () => {
    dialog.value?.open();
  },
  reset: () => {
    tagsToAdd.value = [];
    tagsToRemove.value = [];
    selectedCategory.value = undefined;
    selectedSubcategory.value = undefined;
    isProcessing.value = false;
  },
  isProcessing,
});
</script>
